// Import audit status and entity type constants
import {
  AuditDisableMessages,
  AuditStatus,
  AuditTypeOptions,
  EntityType,
} from 'src/contstant/commonLabels';
// Import audit type definition
import { Audit } from 'src/types/helios/Audit';
// Import question response type definition
import { AuditQuestionItemResponse } from 'src/types/helios/QuestionResponse';
// Import question type definition
import { Question, MediaResponse } from 'src/types/helios/Question';
// Import lodash compact utility to remove falsy values
import { compact, isEqual, toLower } from 'lodash';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import { checkFeaturePermission } from 'src/utils/generalUtils';
// Import FileMetadata interface for document file display
import { FileMetadata } from 'src/components/FileDisplay';
// Import isImageExtension to filter non-image files
import { isImageExtension, AUDIT_UPLOAD_ACCEPT_TYPES } from 'src/contstant/fileConstants';

/**
 * Removes mediaResponses from question response (output-only field, not for input)
 * @param {AuditQuestionItemResponse} response - Question response to clean
 * @returns {AuditQuestionItemResponse} Cleaned response without mediaResponses
 */
export const removeMediaResponses = (
  response: AuditQuestionItemResponse,
): AuditQuestionItemResponse => {
  const { mediaResponses, ...responseWithoutMedia } = response;

  return {
    ...responseWithoutMedia,
    subResponses:
      response.subResponses?.map((subResponse) => {
        const { mediaResponses: _, ...subResponseWithoutMedia } = subResponse;
        return subResponseWithoutMedia;
      }) || [],
  };
};

/**
 * Update audit status to in-progress if it hasn't been started
 */
export const updateAuditToInprogress = (
  audit: Audit, // Current audit object
  updateAudit: (params: any) => void, // Function to update audit
  getUserId: () => string, // Function to get current user ID
) => {
  // Check if audit hasn't been started or is scheduled
  if (
    audit.status === AuditStatus.NOT_STARTED || // Audit not started
    audit.status === AuditStatus.SCHEDULED // Audit is scheduled
  ) {
    // Update audit status to in-progress
    updateAudit({
      auditId: audit.auditId, // Pass audit ID
      status: AuditStatus.IN_PROGRESS, // Set status to in-progress
      businessUnitId: audit.businessUnitId!, // Pass business unit ID
      lastUpdatedBy: getUserId(), // Set current user as last updater
    });
  }
};

/**
 * Update question or sub-question answer
 */
export const updateQuestionAnswer = (
  questionId: string, // ID of the question being updated
  questionResponse: AuditQuestionItemResponse, // Current question response
  updateQuestionResponse: (response: any) => void, // Function to update response
  userId: string, // Current user ID
  currentQuestion?: Question, // Optional current question object
  result?: string, // Optional answer result
  subQuestionId?: string, // Optional sub-question ID
) => {
  // Clean mediaResponses from response at the beginning
  const responseWithoutMedia = removeMediaResponses(questionResponse);

  // Create copy of existing sub-responses or empty array
  let subResponses = [...(responseWithoutMedia?.subResponses ?? [])];

  // Handle sub-question update if subQuestionId provided
  if (subQuestionId) {
    // Find index of existing sub-response
    const index = subResponses.findIndex((item) => item.subQuestionId === subQuestionId);

    // Create sub-response data object
    const subData = {
      ...(subResponses[index] || {}), // Spread existing data
      result, // Set result
      subQuestionId, // Set sub-question ID
      respondedBy: userId, // Set responder
    };
    // Update existing or add new sub-response
    index !== -1 ? (subResponses[index] = subData) : subResponses.push(subData);
  }

  //Clear all sub-question results when main question result is cleared
  // This ensures sub-questions don't retain answers when the parent question is reset
  if (!result && subResponses && !subQuestionId) {
    subResponses = subResponses.map((item) => ({
      ...item,
      result: undefined,
      naReason: undefined,
      naReasonId: undefined,
    }));
  }

  const updatedResponse = {
    ...responseWithoutMedia, // Spread cleaned response
    questionId, // Set question ID
    responseResult: subQuestionId ? undefined : result, // Set result only for main question
    response: {
      ...responseWithoutMedia.response, // Spread existing response data
      mediaIds: [...(responseWithoutMedia?.response.mediaIds || [])], // Copy media IDs
      taskIds: compact([currentQuestion?.localizedContent.content?.taskDetails?.taskId]), // Add task ID if exists
      // Clear NA reason when main question result changes away from NA
      ...(!subQuestionId && result !== 'NA'
        ? { naReason: undefined, naReasonId: undefined }
        : {}),
    },
    subResponses: subQuestionId
      ? subResponses.map((sub) =>
          sub.subQuestionId === subQuestionId && result !== 'NA'
            ? { ...sub, naReason: undefined, naReasonId: undefined }
            : sub,
        )
      : subResponses, // Set updated sub-responses
    respondedBy: userId, // Set responder
    expectedLastUpdatedAt: responseWithoutMedia.lastUpdatedAt, // Optimistic lock
  };

  // Update the question response
  updateQuestionResponse(updatedResponse);
};

/**
 * Get main question status based on sub-questions
 */
export const getMainQuestionStatus = (
  questionResponse?: AuditQuestionItemResponse, // Optional question response
  currentQuestion?: Question, // Optional current question
): string | undefined => {
  // Return undefined if required data is missing
  if (!questionResponse || !currentQuestion) {
    return;
  }

  // If no sub-questions exist, return main response result
  if (!currentQuestion?.localizedContent.content.subQuestions?.length) {
    return questionResponse?.responseResult;
  }

  // ERT: Self-scoring parent (0 P/F subs) or conditional parent — return responseResult directly
  // These parents are answered directly, not derived from sub-questions
  const subQuestions = currentQuestion.localizedContent.content.subQuestions;
  const hasPassFailSubs = subQuestions.some(
    (sq) => !sq.responseType || sq.responseType === 'PASS_FAIL',
  );
  if (!hasPassFailSubs || currentQuestion.hasConditionalSubQuestions) {
    return questionResponse?.responseResult;
  }

  // Count sub-responses that match main response result (P/F subs only)
  const passFailSubCount = subQuestions.filter(
    (sq) => !sq.responseType || sq.responseType === 'PASS_FAIL',
  ).length;
  const matchingSubResponsesCount =
    questionResponse?.subResponses?.filter(
      (item) => item.result === questionResponse.responseResult,
    ).length ?? 0;

  // Return result only if all P/F sub-questions match main result
  return matchingSubResponsesCount === passFailSubCount
    ? questionResponse?.responseResult
    : undefined;
};

/**
 * Process file upload and convert to required format
 */
export const processFileUpload = async (
  files: File[], // Array of files to process
): Promise<{ image: string; extension: string; fileName: string }[]> => {
  // Process all files concurrently
  return Promise.all(
    files.map(
      (
        file, // For each file
      ) =>
        new Promise<{ image: string; extension: string; fileName: string }>((resolve) => {
          const reader = new FileReader(); // Create file reader
          reader.onload = () =>
            // When file is loaded
            resolve({
              image: reader.result as string,
              extension: file.type,
              fileName: file.name, // Include original file name
            });
          reader.readAsDataURL(file); // Start reading file as data URL
        }),
    ),
  );
};

/**
 * Handle file upload Handle file upload for audit questions and update question response
 */
export const handleFileUpload = async (
  files: File[], // Files to upload
  uploadfiles: (fileData: any, params: any) => Promise<string[]>, // Upload function
  programId: string, // Program ID
  userId: string, // Current user ID

  updateQuestionResponse: (response: any) => void, // Function to update response
  currentQuestion?: Question, // Optional current question
  questionResponseItem?: AuditQuestionItemResponse, // Optional question response
  subQuestionId?: string, // Optional sub-question ID for sub-question uploads
  enableEnhancedResponseTypes?: boolean, // ERT flag for responseResult preservation
) => {
  // Exit early if required data is missing
  if (!currentQuestion || !questionResponseItem) return;

  // Clean mediaResponses from response at the beginning
  const responseWithoutMedia = removeMediaResponses(questionResponseItem);

  // Process files to get data URLs and extensions
  const fileData = await processFileUpload(files);

  // Upload files and get media IDs
  const uploadedMediaIds = await uploadfiles(fileData, {
    programId: programId, // Set program ID
    businessUnit: currentQuestion.businessUnit, // Set business unit
    entityType: EntityType.ANSWER, // Set entity type as answer
    createdBy: userId, // Set creator
  });

  // Determine whether to send responseResult or let backend derive:
  // - Flag OFF: original behavior (send undefined when subs exist)
  // - Flag ON + P/F subs exist: send undefined → backend derives from sub results
  // - Flag ON + no P/F subs (conditional/self-scoring): preserve existing responseResult
  let responseResult: string | undefined;
  if (enableEnhancedResponseTypes) {
    const hasPassFailSubs = responseWithoutMedia?.subResponses?.some(
      (sub) => !sub.responseType || sub.responseType === 'PASS_FAIL',
    );
    responseResult = hasPassFailSubs ? undefined : responseWithoutMedia.responseResult;
  } else {
    responseResult =
      responseWithoutMedia?.subResponses?.length > 0
        ? undefined
        : responseWithoutMedia.responseResult;
  }

  // Handle sub-question media upload
  if (subQuestionId) {
    const updatedSubResponses = responseWithoutMedia.subResponses?.map((subResponse) => {
      if (subResponse.subQuestionId === subQuestionId) {
        return {
          ...subResponse,
          mediaIds: [...(subResponse.mediaIds || []), ...uploadedMediaIds],
        };
      }
      return subResponse;
    });

    updateQuestionResponse({
      ...responseWithoutMedia,
      responseResult,
      subResponses: updatedSubResponses,
      respondedBy: userId,
    });
    return;
  }

  // Update main question response with new media IDs
  updateQuestionResponse({
    ...responseWithoutMedia,
    responseResult,
    response: {
      ...responseWithoutMedia?.response,
      mediaIds: [
        ...(responseWithoutMedia?.response?.mediaIds || []),
        ...uploadedMediaIds,
      ],
    },
    respondedBy: userId,
  });
};

/**
 * Initialize comments from question responses
 */
export const initializeComments = (
  auditQuestionResponses: AuditQuestionItemResponse[], // Array of question responses
  existingComments: Record<string, string>, // Existing comments map
): Record<string, string> => {
  // Initialize empty comments object
  const newComments: Record<string, string> = {};

  // Iterate through each question response
  auditQuestionResponses.forEach((qs) => {
    // Add main question comment if not already exists
    if (!existingComments[qs.questionId]) {
      newComments[qs.questionId] = qs.response?.comment ?? ' '; // Use existing comment or space
    }
    // Iterate through sub-responses
    qs?.subResponses?.forEach((subQ) => {
      // Add sub-question comment if not already exists
      if (!existingComments[subQ.subQuestionId]) {
        newComments[subQ.subQuestionId] = subQ?.comment ?? ' '; // Use existing comment or space
      }
    });
  });

  // Return new comments object
  return newComments;
};

/**
 * Update question response after media deletion
 */
export const updateResponseAfterMediaDeletion = (
  mediaId: string, // ID of media to delete
  updateQuestionResponse: (response: any) => void, // Function to update response
  userId: string, // Current user ID
  auditQuestionResponses?: AuditQuestionItemResponse, // Optional question response
  subQuestionId?: string, // Optional sub-question ID for sub-question media deletion
  enableEnhancedResponseTypes?: boolean, // ERT flag for responseResult preservation
) => {
  // Exit early if no response data
  if (!auditQuestionResponses) {
    return;
  }

  // Clean mediaResponses from response at the beginning
  const responseWithoutMedia = removeMediaResponses(auditQuestionResponses);

  // Determine whether to send responseResult or let backend derive:
  // - Flag OFF: original behavior (send undefined when subs exist)
  // - Flag ON + P/F subs exist: send undefined → backend derives from sub results
  // - Flag ON + no P/F subs (conditional/self-scoring): preserve existing responseResult
  let responseResult: string | undefined;
  if (enableEnhancedResponseTypes) {
    const hasPassFailSubs = responseWithoutMedia?.subResponses?.some(
      (sub) => !sub.responseType || sub.responseType === 'PASS_FAIL',
    );
    responseResult = hasPassFailSubs ? undefined : responseWithoutMedia.responseResult;
  } else {
    responseResult =
      responseWithoutMedia?.subResponses?.length > 0
        ? undefined
        : responseWithoutMedia.responseResult;
  }

  // Handle sub-question media deletion
  if (subQuestionId) {
    const updatedSubResponses = responseWithoutMedia.subResponses?.map((subResponse) => {
      if (subResponse.subQuestionId === subQuestionId) {
        return {
          ...subResponse,
          mediaIds: subResponse.mediaIds?.filter((id) => id !== mediaId) || [],
        };
      }
      return subResponse;
    });

    updateQuestionResponse({
      ...responseWithoutMedia,
      responseResult,
      subResponses: updatedSubResponses,
      respondedBy: userId,
    });
    return;
  }

  // Update response by removing deleted media ID from main question
  updateQuestionResponse({
    ...responseWithoutMedia,
    responseResult,
    response: {
      ...responseWithoutMedia.response,
      mediaIds:
        responseWithoutMedia.response.mediaIds?.filter((id) => id !== mediaId) || [],
    },
    respondedBy: userId,
  });
};

/**
 * Helper function to extract file extension from fileName or mediaId
 * Returns empty string if no extension is found
 */
const getFileExtension = (fileName: string | undefined, mediaId: string): string => {
  const name = fileName || mediaId;
  const lastDotIndex = name.lastIndexOf('.');
  return lastDotIndex !== -1 ? name.substring(lastDotIndex) : '';
};

/**
 * Image gallery item structure (matches ImageGallery component interface)
 */
export interface ImageGalleryItem {
  id: string;
  fileName?: string;
  imageUrl?: string;
}

/**
 * Helper function to filter and map media responses based on file type
 * @param mediaResponses - Array of media responses to process
 * @param isImageFilter - True to get images, false to get documents
 * @param mapToImageFormat - True to map to ImageGallery format, false for FileMetadata format
 */
const filterAndMapMediaFiles = <T extends ImageGalleryItem | FileMetadata>(
  mediaResponses: MediaResponse[] | undefined,
  isImageFilter: boolean,
  mapToImageFormat: boolean,
): T[] => {
  return (
    mediaResponses
      ?.filter((media) => {
        const extension = getFileExtension(media.media.fileName, media.media.mediaId);
        return isImageFilter ? isImageExtension(extension) : !isImageExtension(extension);
      })
      .map((media) => {
        const fileName = media.media.fileName || media.media.mediaId;
        if (mapToImageFormat) {
          return {
            id: media.media.mediaId,
            fileName,
            imageUrl: media.presignedUrl,
          } as T;
        }
        return {
          fileName,
          mediaId: media.media.mediaId,
          presignedUrl: media.presignedUrl,
          extension: media.media.extension,
        } as T;
      }) || []
  );
};

/**
 * Get media files (image files only) for current question
 */
export const getImageFiles = (
  currentQuestion?: Question,
  auditQuestionResponse?: AuditQuestionItemResponse,
): ImageGalleryItem[] => {
  if (!currentQuestion || !auditQuestionResponse) return [];
  return filterAndMapMediaFiles<ImageGalleryItem>(
    auditQuestionResponse?.mediaResponses?.response,
    true, // Get images
    true, // Map to ImageGallery format
  );
};

/**
 * Get media files (image files only) for a specific sub-question
 */
export const getSubQuestionImageFiles = (
  subQuestionId: string,
  auditQuestionResponse?: AuditQuestionItemResponse,
): ImageGalleryItem[] => {
  if (!auditQuestionResponse) return [];
  const subResponse = auditQuestionResponse.subResponses?.find(
    (sr) => sr.subQuestionId === subQuestionId,
  );
  return filterAndMapMediaFiles<ImageGalleryItem>(
    subResponse?.mediaResponses?.response,
    true, // Get images
    true, // Map to ImageGallery format
  );
};

/**
 * Get document files (non-image files) for the main question
 * Filters out image files and returns FileMetadata format for FileDisplay component
 */
export const getDocumentFiles = (
  currentQuestion?: Question,
  auditQuestionResponse?: AuditQuestionItemResponse,
): FileMetadata[] => {
  if (!currentQuestion || !auditQuestionResponse) return [];
  return filterAndMapMediaFiles<FileMetadata>(
    auditQuestionResponse?.mediaResponses?.response,
    false, // Get documents
    false, // Map to FileMetadata format
  );
};

/**
 * Get document files (non-image files) for a specific sub-question
 * Filters out image files and returns FileMetadata format for FileDisplay component
 */
export const getSubQuestionDocumentFiles = (
  subQuestionId: string,
  auditQuestionResponse?: AuditQuestionItemResponse,
): FileMetadata[] => {
  if (!auditQuestionResponse) return [];
  const subResponse = auditQuestionResponse.subResponses?.find(
    (sr) => sr.subQuestionId === subQuestionId,
  );
  return filterAndMapMediaFiles<FileMetadata>(
    subResponse?.mediaResponses?.response,
    false, // Get documents
    false, // Map to FileMetadata format
  );
};

/**
 * Get comments for question or sub-question
 */
export const getComments = (
  auditQuestionResponse?: AuditQuestionItemResponse, // Optional question response
  subQuestionId?: string, // Optional sub-question ID
): string => {
  // Handle sub-question comment retrieval
  if (subQuestionId) {
    return (
      auditQuestionResponse?.subResponses?.find(
        (item) => item.subQuestionId === subQuestionId, // Find matching sub-question
      )?.comment || '' // Return comment or empty string
    );
  }
  // Return main question comment or empty string
  return auditQuestionResponse?.response?.comment || '';
};

/**
 * Set comments for question or sub-question
 */
export const setComments = (
  value: string, // Comment value to set
  updateQuestionResponseList: (response: AuditQuestionItemResponse) => void, // Function to update response
  userId: string, // Current user ID
  subQuestionId?: string, // Optional sub-question ID
  auditQuestionResponse?: AuditQuestionItemResponse, // Optional question response
) => {
  // Exit early if no response data
  if (!auditQuestionResponse) return;

  // Create updated response object
  const updatedResponse: AuditQuestionItemResponse = {
    ...auditQuestionResponse, // Spread existing response
    subResponses: auditQuestionResponse.subResponses
      ? [...auditQuestionResponse.subResponses] // Copy sub-responses if they exist
      : [], // Or create empty array
    response: { ...auditQuestionResponse.response }, // Copy response data
    respondedBy: userId, // Set responder
  };

  // Handle sub-question comment update
  if (subQuestionId) {
    // Find index of sub-question to update
    const subIndex = updatedResponse.subResponses.findIndex(
      (item) => item.subQuestionId === subQuestionId,
    );
    // Update sub-question comment if found
    if (subIndex !== -1) {
      updatedResponse.subResponses[subIndex] = {
        ...updatedResponse.subResponses[subIndex], // Spread existing sub-response
        comment: value, // Set new comment value
      };
    }
  } else {
    // Update main question comment
    updatedResponse.response.comment = value;
  }

  // Update the response list with modified response
  updateQuestionResponseList(updatedResponse);
};

/**
 * Check if user has permission to perform specific audit type
 */
export const hasAuditPermission = (
  auditType: AuditTypeOptions,
  persona: string,
  userPermissions?: any,
): boolean => {
  // Always allow test audits
  if (auditType === AuditTypeOptions.TEST) return true;

  // Map audit types to their permission configurations
  const permissionMap = {
    [AuditTypeOptions.CALIBRATION]:
      permissionAccessConfig.CALIBRATION_AUDIT.PERFORM_CALIBRATION_AUDIT,
    [AuditTypeOptions.PROGRESS]:
      permissionAccessConfig.PROGRESS_AUDIT.PERFORM_PROGRESS_AUDIT,
    [AuditTypeOptions.SITUATION]:
      permissionAccessConfig.SITUATION_AUDIT.PERFORM_SITUATION_AUDIT,
    [AuditTypeOptions.MPA]: permissionAccessConfig.MPA_AUDIT.PERFORM_MPA_AUDIT,
  };
  // Check if user has required permission for audit type
  return checkFeaturePermission(
    persona,
    permissionMap[auditType],
    userPermissions?.permissions,
  );
};

/**
 * Check if user has permission to reopen specific audit type
 */
export const hasReopenAuditPermission = (
  auditType: AuditTypeOptions,
  persona: string,
  userPermissions?: any,
): boolean => {
  // Always allow test audits
  if (auditType === AuditTypeOptions.TEST) return true;

  // Map audit types to their reopen permission configurations
  const permissionMap = {
    [AuditTypeOptions.CALIBRATION]:
      permissionAccessConfig.CALIBRATION_AUDIT.REOPEN_CALIBRATION_AUDIT,
    [AuditTypeOptions.PROGRESS]:
      permissionAccessConfig.PROGRESS_AUDIT.REOPEN_PROGRESS_AUDIT,
    [AuditTypeOptions.SITUATION]:
      permissionAccessConfig.SITUATION_AUDIT.REOPEN_SITUATION_AUDIT,
    [AuditTypeOptions.MPA]: permissionAccessConfig.MPA_AUDIT.REOPEN_MPA_AUDIT,
  };
  // Check if user has required reopen permission for audit type
  return checkFeaturePermission(
    persona,
    permissionMap[auditType],
    userPermissions?.permissions,
  );
};

/**
 * Check if user can reopen the current audit
 */
export const getIsReopenAudit = (
  persona: string, // User persona
  userPermissions?: any, // User permissions object
  currentAudit?: Audit, // Current audit data
): boolean => {
  // Check reopen permission if audit type exists, otherwise return false
  return currentAudit?.auditType
    ? hasReopenAuditPermission(
        currentAudit.auditType as AuditTypeOptions,
        persona,
        userPermissions,
      )
    : false;
};

/**
 * Determines if user can perform the current audit
 */
export const getIsPerformAudit = (
  persona: string,
  userPermissions?: any,
  currentAudit?: Audit,
): boolean => {
  // Check perform permission if audit type exists, otherwise return false
  return currentAudit?.auditType
    ? hasAuditPermission(
        currentAudit.auditType as AuditTypeOptions,
        persona,
        userPermissions,
      )
    : false;
};

/**
 * Gets the effective assignees for an audit.
 * Reads from the new `assignees` field if it exists, otherwise falls back
 * to the legacy `assignedTo` field for old records.
 * Returns an array of usernames (may be empty).
 */
export const getAuditAssignees = (audit?: Audit): string[] => {
  if (!audit) return [];
  if (audit.assignees && audit.assignees.length > 0) {
    return audit.assignees;
  }
  // Fallback to legacy assignedTo field for old records
  return audit.assignedTo ? [audit.assignedTo] : [];
};

/**
 * Gets the primary (first) assignee for display purposes.
 * Reads from assignees first, falls back to assignedTo for old records.
 */
export const getPrimaryAssignee = (audit?: Audit): string | undefined => {
  const assignees = getAuditAssignees(audit);
  return assignees.length > 0 ? assignees[0] : undefined;
};

/**
 * Gets a display string for the audit assignees.
 * Returns comma-separated usernames or '-' if none assigned.
 */
export const getAssigneesDisplayValue = (audit?: Audit): string => {
  const assignees = getAuditAssignees(audit);
  return assignees.length > 0 ? assignees.join(', ') : '-';
};

/**
 * Get the disable reason for audit actions based on current state
 */
export const getAuditDisableReason = (
  userID: string,
  isPerformAudit: boolean,
  currentAudit?: Audit,
): string => {
  // Return appropriate disable message based on audit state
  if (currentAudit?.status === AuditStatus.COMPLETED) {
    return AuditDisableMessages.AUDIT_COMPLETED;
  }

  const assignees = getAuditAssignees(currentAudit);

  if (assignees.length === 0) {
    return AuditDisableMessages.NO_ASSIGNEE;
  }

  const userIsAssignee = assignees.some((a) => isEqual(toLower(a), toLower(userID)));
  if (!userIsAssignee) {
    return assignees.length === 1
      ? AuditDisableMessages.WRONG_ASSIGNEE.replace('{assignee}', assignees[0])
      : AuditDisableMessages.WRONG_ASSIGNEE_MULTI;
  }

  if (!isPerformAudit) {
    return AuditDisableMessages.NO_PERMISSION;
  }
  return ''; // No disable reason - action is allowed
};
